import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { endpoints, formatApiError } from "../../lib/api";
import { TypeBadge } from "../../lib/accountTypes";
import { HealthChip } from "../../components/ContactHealth";
import { daysAgo } from "../../lib/crmHealth";
import {
    Handshake, ArrowsClockwise, Phone, Buildings, Shield, Funnel, X,
    Kanban, ListBullets, MagnifyingGlass, CaretUp, CaretDown,
} from "@phosphor-icons/react";

// "active" (real invoiced work underway) is intentionally left off the
// Kanban board — it's a backend-driven state, surfaced only in the List view.
const BOARD_STAGES = ["lead", "contacted", "proposal", "negotiation", "won", "closed_lost"];
const STAGES = ["lead", "contacted", "proposal", "negotiation", "won", "active", "closed_lost"];
const STAGE_LABELS = {
    lead: "Lead", contacted: "Contacted", proposal: "Proposal", negotiation: "Negotiation",
    won: "On Supplier List (Won)", active: "Active (Being Invoiced)", closed_lost: "Lost",
};
const STAGE_ACCENT = {
    lead: "#64748b", contacted: "#0891b2", proposal: "var(--rom-orange)",
    negotiation: "#d97706", won: "var(--rom-green)", active: "#2563eb", closed_lost: "#94a3b8",
};
const CARDS_PER_COLUMN = 5;

export default function SalesBoard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const filterOn = searchParams.get("filter") === "followups";
    const [view, setView] = useState("board");
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dragId, setDragId] = useState(null);
    const [dragOver, setDragOver] = useState(null);
    const [expanded, setExpanded] = useState(new Set());
    const [q, setQ] = useState("");
    const [stageFilter, setStageFilter] = useState("all");
    const [sort, setSort] = useState({ key: "updated_at", dir: "desc" });
    const nameById = useMemo(() => Object.fromEntries(clients.map((x) => [x.id, x.name])), [clients]);
    const [healthById, setHealthById] = useState({});

    async function load() {
        setLoading(true);
        try { const { data } = await endpoints.clients(); setClients(data || []); }
        catch { /* ignore */ }
        try { const { data } = await endpoints.crmOverview(); setHealthById(Object.fromEntries((data?.accounts || []).map((a) => [a.id, a]))); }
        catch { /* optional */ }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const todayStr = new Date().toISOString().slice(0, 10);
    const hasFollowupDue = (c) => {
        const openTaskDue = (c.tasks || []).some((t) => (t.status || "open") === "open" && (!t.due_date || String(t.due_date).slice(0, 10) <= todayStr));
        const apptDue = (c.appointments || []).some((a) => (a.status || "scheduled") === "scheduled" && String(a.date || "").slice(0, 10) <= todayStr);
        return openTaskDue || apptDue;
    };
    // Item-level count (tasks + appointments), matching the Action Center
    // tile's exact tally so the number shown here never drifts from the one
    // the user clicked.
    const followupItemCount = clients.reduce((n, c) => {
        const taskHits = (c.tasks || []).filter((t) => (t.status || "open") === "open" && (!t.due_date || String(t.due_date).slice(0, 10) <= todayStr)).length;
        const apptHits = (c.appointments || []).filter((a) => (a.status || "scheduled") === "scheduled" && String(a.date || "").slice(0, 10) <= todayStr).length;
        return n + taskHits + apptHits;
    }, 0);

    const displayClients = filterOn ? clients.filter(hasFollowupDue) : clients;
    const clearFilter = () => setSearchParams({});

    const byStage = useMemo(() => {
        const m = {};
        BOARD_STAGES.forEach((s) => { m[s] = []; });
        displayClients.forEach((c) => { const s = c.stage || "lead"; if (m[s]) m[s].push(c); });
        Object.keys(m).forEach((s) => m[s].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")));
        return m;
    }, [displayClients]);

    async function moveTo(clientId, stage) {
        const c = clients.find((x) => x.id === clientId);
        if (!c || c.stage === stage) return;
        setClients((cs) => cs.map((x) => x.id === clientId ? { ...x, stage } : x));
        try { await endpoints.patchClient(clientId, { stage }); }
        catch (e) { alert(formatApiError(e)); load(); }
    }

    function toggleExpand(stage) {
        setExpanded((s) => {
            const n = new Set(s);
            if (n.has(stage)) n.delete(stage); else n.add(stage);
            return n;
        });
    }

    // ── List view ──────────────────────────────────────────────────────
    const listRows = useMemo(() => {
        let rows = displayClients;
        if (stageFilter !== "all") rows = rows.filter((c) => (c.stage || "lead") === stageFilter);
        if (q) {
            const hay = q.toLowerCase();
            rows = rows.filter((c) => {
                const primary = c.contacts?.find((x) => x.is_primary) || c.contacts?.[0];
                return `${c.name} ${primary?.name || c.contact_name || ""} ${c.email || ""} ${c.address || ""}`.toLowerCase().includes(hay);
            });
        }
        rows = [...rows].sort((a, b) => {
            const av = a[sort.key] ?? "";
            const bv = b[sort.key] ?? "";
            const dir = sort.dir === "asc" ? 1 : -1;
            return String(av).localeCompare(String(bv)) * dir;
        });
        return rows;
    }, [displayClients, stageFilter, q, sort]);

    function setSortKey(k) {
        if (sort.key === k) setSort({ key: k, dir: sort.dir === "asc" ? "desc" : "asc" });
        else setSort({ key: k, dir: "asc" });
    }

    return (
        <div className="px-6 py-8 space-y-5" data-testid="sales-board-page">
            <header className="flex flex-wrap items-end justify-between gap-4 max-w-[1700px]">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">CRM</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <Handshake size={28} weight="bold" className="text-[var(--rom-orange)]" /> Customer pipeline
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">{displayClients.length} accounts · speak to the client → proposal → negotiate → on supplier list → ROM when disaster strikes.</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-[6px] border border-[var(--rom-border-strong)] overflow-hidden" data-testid="pipeline-view-toggle">
                        <button onClick={() => setView("board")} data-testid="view-toggle-board"
                            className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 transition-colors ${view === "board" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)] hover:bg-slate-50"}`}>
                            <Kanban size={13} weight="bold" /> Board
                        </button>
                        <button onClick={() => setView("list")} data-testid="view-toggle-list"
                            className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 border-l border-[var(--rom-border-strong)] transition-colors ${view === "list" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)] hover:bg-slate-50"}`}>
                            <ListBullets size={13} weight="bold" /> List
                        </button>
                    </div>
                    <button onClick={load} className="btn btn-ghost" data-testid="sales-board-refresh"><ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
                </div>
            </header>

            {filterOn && (
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-[6px] border border-[var(--rom-border-strong)] bg-slate-50 text-sm max-w-[1700px]" data-testid="crm-followup-filter-banner">
                    <Funnel size={14} weight="bold" className="text-[var(--rom-text-2)]" />
                    <span className="font-semibold">Showing accounts with follow-ups due</span>
                    <span className="text-[var(--rom-text-3)]">({followupItemCount})</span>
                    <button onClick={clearFilter} className="ml-auto text-xs font-semibold text-[var(--rom-text-2)] hover:underline flex items-center gap-1" data-testid="crm-clear-filter-btn"><X size={12} weight="bold" /> Clear filter</button>
                </div>
            )}

            {view === "board" ? (
                <div className="overflow-x-auto pb-4">
                    <div className="flex gap-4 min-w-fit">
                        {BOARD_STAGES.map((stage) => {
                            const cards = byStage[stage] || [];
                            const isExpanded = filterOn || expanded.has(stage);
                            const visible = isExpanded ? cards : cards.slice(0, CARDS_PER_COLUMN);
                            const hiddenCount = cards.length - visible.length;
                            return (
                                <div key={stage}
                                    data-testid={`sales-column-${stage}`}
                                    onDragOver={(e) => { e.preventDefault(); setDragOver(stage); }}
                                    onDragLeave={() => setDragOver((d) => d === stage ? null : d)}
                                    onDrop={() => { if (dragId) moveTo(dragId, stage); setDragId(null); setDragOver(null); }}
                                    className={`w-[260px] shrink-0 rounded-[8px] border transition-colors ${dragOver === stage ? "border-[var(--rom-orange)] bg-[var(--rom-orange-soft)]/30" : "border-[var(--rom-border)] bg-slate-50/60"}`}
                                >
                                    <div className="px-3 py-2.5 border-b border-[var(--rom-border)] flex items-center justify-between sticky top-0 bg-white/90 backdrop-blur rounded-t-[8px]" style={{ borderTop: `3px solid ${STAGE_ACCENT[stage]}` }}>
                                        <div className="text-xs font-bold uppercase tracking-wider" style={{ color: STAGE_ACCENT[stage] }}>{STAGE_LABELS[stage]}</div>
                                        <span className="chip chip-gray text-[10px]">{cards.length}</span>
                                    </div>
                                    <div className="p-2 space-y-2 min-h-[120px]">
                                        {visible.map((c) => (
                                            <div key={c.id}
                                                data-testid={`sales-card-${c.id}`}
                                                draggable
                                                onDragStart={() => setDragId(c.id)}
                                                onDragEnd={() => { setDragId(null); setDragOver(null); }}
                                                onClick={() => navigate(`/crm/accounts/${c.id}`)}
                                                className="bg-white border border-[var(--rom-border)] rounded-[6px] p-3 cursor-pointer hover:shadow-md hover:border-[var(--rom-navy)] transition-all"
                                            >
                                                <div className="flex items-center gap-1.5 text-[10px] text-[var(--rom-text-3)] uppercase font-semibold">
                                                    <TypeBadge type={c.type} size={11} />
                                                    {c.parent_id && nameById[c.parent_id] && <span className="normal-case font-normal truncate">· {nameById[c.parent_id]}</span>}
                                                </div>
                                                <div className="font-semibold text-sm mt-1 leading-snug line-clamp-2">{c.name}</div>
                                                {(c.contacts?.find((x) => x.is_primary) || c.contacts?.[0]) && (
                                                    <div className="text-[11px] text-[var(--rom-text-2)] mt-1 truncate">{(c.contacts.find((x) => x.is_primary) || c.contacts[0]).name}</div>
                                                )}
                                                {!c.contacts?.length && c.contact_name && (
                                                    <div className="text-[11px] text-[var(--rom-text-2)] mt-1 truncate">{c.contact_name}</div>
                                                )}
                                                {c.phone && <div className="text-[10px] text-[var(--rom-text-3)] mt-1 flex items-center gap-1"><Phone size={10} /> {c.phone}</div>}
                                                {(c.tasks?.some((t) => (t.status || "open") === "open") || ["overdue", "never", "due_soon"].includes(healthById[c.id]?.status)) && (
                                                    <div className="mt-2 flex flex-wrap gap-1">
                                                        {c.tasks?.some((t) => (t.status || "open") === "open") && <span className="chip chip-orange text-[10px]" style={{ padding: "1px 6px" }}>Action pending</span>}
                                                        {["overdue", "never", "due_soon"].includes(healthById[c.id]?.status) && <HealthChip health={healthById[c.id]} compact />}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {cards.length === 0 && <div className="text-[11px] text-[var(--rom-text-3)] text-center py-4">Drop here</div>}
                                        {!filterOn && cards.length > CARDS_PER_COLUMN && (
                                            <button onClick={() => toggleExpand(stage)} data-testid={`stage-toggle-${stage}`}
                                                className="w-full text-center text-[11px] font-semibold text-[var(--rom-navy)] hover:underline py-1.5">
                                                {isExpanded ? "Show less" : `Show all (${hiddenCount} more)`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="space-y-3 max-w-[1700px]" data-testid="pipeline-list-view">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-1 max-w-sm">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rom-text-3)]" />
                            <input className="input pl-9" placeholder="Search name, contact, address…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pipeline-search" />
                        </div>
                        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="pipeline-stage-filter"
                            className="h-9 px-3 rounded-[4px] border border-[var(--rom-border-strong)] text-sm font-semibold bg-white">
                            <option value="all">All stages</option>
                            {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                        </select>
                    </div>
                    <div className="rom-card overflow-hidden">
                        <table className="rom-table" data-testid="pipeline-list-table">
                            <thead>
                                <tr>
                                    <th><button onClick={() => setSortKey("name")} className="flex items-center gap-1">Name {sort.key === "name" && (sort.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}</button></th>
                                    <th>Type</th>
                                    <th>Primary contact</th>
                                    <th>Stage</th>
                                    <th>Last contact</th>
                                    <th><button onClick={() => setSortKey("updated_at")} className="flex items-center gap-1">Last updated {sort.key === "updated_at" && (sort.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}</button></th>
                                </tr>
                            </thead>
                            <tbody>
                                {listRows.length === 0 && (
                                    <tr><td colSpan={6} className="text-center text-[var(--rom-text-3)] py-10">No accounts match.</td></tr>
                                )}
                                {listRows.map((c) => {
                                    const primary = c.contacts?.find((x) => x.is_primary) || c.contacts?.[0];
                                    return (
                                        <tr key={c.id} className="cursor-pointer" onClick={() => navigate(`/crm/accounts/${c.id}`)} data-testid={`pipeline-row-${c.id}`}>
                                            <td className="font-semibold">{c.name}</td>
                                            <td className="capitalize text-xs text-[var(--rom-text-2)]">
                                                <span className="flex items-center gap-1.5"><TypeBadge type={c.type} size={12} />{c.parent_id && nameById[c.parent_id] && <span className="normal-case text-[var(--rom-text-3)]">· {nameById[c.parent_id]}</span>}</span>
                                            </td>
                                            <td className="text-sm text-[var(--rom-text-2)]">{primary?.name || c.contact_name || "—"}</td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <select value={c.stage || "lead"} onChange={(e) => moveTo(c.id, e.target.value)} data-testid={`pipeline-stage-select-${c.id}`}
                                                    className="h-7 px-2 rounded-[4px] border border-[var(--rom-border-strong)] text-xs font-semibold bg-white" style={{ color: STAGE_ACCENT[c.stage || "lead"] }}>
                                                    {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                                                </select>
                                            </td>
                                            <td className="text-xs text-[var(--rom-text-2)]" data-testid={`pipeline-contact-${c.id}`}>
                                                <span className="flex items-center gap-1.5">{healthById[c.id]?.last_contact_at ? daysAgo(healthById[c.id].days_since_contact) : "Never"} <HealthChip health={healthById[c.id]} compact /></span>
                                            </td>
                                            <td className="text-xs text-[var(--rom-text-2)]">{fmtDate(c.updated_at)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function fmtDate(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString(); } catch { return ""; }
}
